import { useCallback, useEffect, useReducer, useContext, type Dispatch } from 'react';

import { ExperienceTrackerContext } from '@atlassian/experience-tracker';

import { fg } from '@atlaskit/platform-feature-flags';

import { ExperienceNames, ExperienceReasons } from '../experience-tracker/ExperienceNames';
import { useUniqueKey } from '../unique-key-provider/UniqueKeyProvider';

/** NOTE:
 * The STORAGE_ACCESS and LOGIN_CONFIRMATION subjects mirror the ones in [Confluence Monolith](https://stash.atlassian.com/projects/CONFCLOUD/repos/confluence/browse/confluence-core/confluence-webapp/src/main/webapp/embeddedconfluence/)
 * If there are changes needed, they should be applied in both places.
 */
export enum Subjects {
	STORAGE_ACCESS = 'storage-access',
	LOGIN_CONFIRMATION = 'login-confirmation',
	CONTENT_LOAD = 'content-load',
}

export enum LoadingStatus {
	IFRAME_LOADED = 'iframe-loaded',
	IFRAME_UNLOADED = 'iframe-unloaded',
	CONTENT_LOAD_STARTED = 'content-load-started',
	REDIRECTED_TO_LOGIN = 'redirected-to-login',
	RELOAD_EP = 'reload-ep',
}

/** Storage Access statuses:
 * These statuses mirror the ones in [Confluence Monolith](confluence-core/confluence-webapp/src/main/webapp/embeddedconfluence/embedded-confluence-storage-access.vm)
 * If there are changes needed, they should be applied in both places.
 */
export enum StorageAccessStatus {
	GRANTED = 'granted',
	REJECTED = 'rejected',
	NOT_SUPPORTED = 'not-supported',
	ERROR = 'error',
}

/** Login statuses:
 * These statuses mirror the ones in [Confluence Monolith](confluence-core/confluence-webapp/src/main/webapp/embeddedconfluence/embedded-confluence-login-confirmation.vm)
 * If there are changes needed, they should be applied in both places.
 */
export enum UserLoginStatus {
	SUCCESS = 'success',
}

/* NOTE: (Refer this table for understanding the behavior
  on different browsers: https://hello.atlassian.net/wiki/spaces/~288141084/pages/2154391293/Conditions+to+load+EP)
*/
function pageLoadValidationReducer(state: any, action: any) {
	//NOTE: We assume that storage access and user login is required at the load of the PageLoadValidation component

	switch (action.subject) {
		case Subjects.STORAGE_ACCESS:
			switch (action.status) {
				case StorageAccessStatus.GRANTED:
					/*If storage access is granted, refresh app, if user is logged in, try to load EP. If not, load Login CTA
					 */
					return {
						...state,
						iframeLoaded: false,
						contentLoadStarted: false,
						shouldAskStorageAccess: false,
					};
				case StorageAccessStatus.REJECTED:
					/*If storage access is reject, it could mean either of the two things:
          1. (For Safari): User has storage access but may not be logged in, Try loggin user in
          2. (For Firefox, Edge): User may be logged in but storage access is not yet granted by the browser
          In both these case, ask user to login.
          */
					return {
						...state,
						iframeLoaded: false,
						contentLoadStarted: false,
						shouldAskStorageAccess: false,
						shouldAskToLogin: true,
					};
				case StorageAccessStatus.NOT_SUPPORTED:
					/* This case is specific for Chrome where storage access is not supported.
          So we can skip asking for storage access and direct user to the Login CTA.
          */
					return {
						...state,
						iframeLoaded: false,
						contentLoadStarted: false,
						shouldAskToLogin: true,
						shouldAskStorageAccess: false,
					};

				case StorageAccessStatus.ERROR:
					/** To catch any other errors while requesting storage access */
					return {
						...state,
					};
			}
			break;
		case Subjects.LOGIN_CONFIRMATION:
			switch (action.status) {
				case UserLoginStatus.SUCCESS:
					/*
          If user has logged in, it means that storage access is granted. Refresh app, Try loading EP
          */
					return {
						...state,
						iframeLoaded: false,
						contentLoadStarted: false,
						shouldAskToLogin: false,
						iframeUnloaded: false,
						redirectedToLogin: false,
					};
			}
			break;
		case Subjects.CONTENT_LOAD:
			//These states are set only when the user has navigated throught the storage access and login flows. So try to load EP
			switch (action.status) {
				case LoadingStatus.IFRAME_LOADED:
					return {
						...state,
						iframeLoaded: true,
					};
				case LoadingStatus.CONTENT_LOAD_STARTED:
					return {
						...state,
						contentLoadStarted: true,
					};
				case LoadingStatus.IFRAME_UNLOADED:
					return {
						...state,
						iframeUnloaded: true,
					};
				case LoadingStatus.REDIRECTED_TO_LOGIN:
					return {
						...state,
						redirectedToLogin: true,
					};
				case LoadingStatus.RELOAD_EP:
					return {
						...state,
						iframeLoaded: false,
						contentLoadStarted: false,
					};
			}
			break;
	}
}

export const useValidationState = (): {
	dispatch: Dispatch<any>;
	state: any;
} => {
	const initialState = {
		iframeLoaded: false,
		contentLoadStarted: false,
		shouldAskStorageAccess: true,
		shouldAskToLogin: true,
		redirectedToLogin: false,
		iframeUnloaded: false,
	};

	const [state, dispatch] = useReducer(pageLoadValidationReducer, initialState);

	const experienceTracker = useContext(ExperienceTrackerContext);

	const { uniqueKey } = useUniqueKey();

	const messageListener = useCallback(
		(messageEvent: MessageEvent) => {
			const { subject, result, embeddedConfluenceUrl } = messageEvent.data ?? {};
			const messageKey =
				embeddedConfluenceUrl && fg('platform_deprecate_lp_cc_embed')
					? new URL(embeddedConfluenceUrl).searchParams.get('uniqueKey')
					: null;
			if (
				!subject ||
				!result ||
				(messageKey &&
					uniqueKey &&
					messageKey !== uniqueKey &&
					fg('platform_deprecate_lp_cc_embed'))
			) {
				return;
			}
			switch (subject) {
				case Subjects.STORAGE_ACCESS:
					switch (result) {
						case StorageAccessStatus.GRANTED:
							experienceTracker.succeed({
								name: ExperienceNames.STORAGE_ACCESS_PAGE,
							});
							dispatch({
								subject: Subjects.STORAGE_ACCESS,
								status: StorageAccessStatus.GRANTED,
							});
							break;
						case StorageAccessStatus.REJECTED:
							experienceTracker.abort({
								name: ExperienceNames.STORAGE_ACCESS_PAGE,
								reason: ExperienceReasons.STORAGE_ACCESS_REJECTED,
							});
							dispatch({
								subject: Subjects.STORAGE_ACCESS,
								status: StorageAccessStatus.REJECTED,
							});
							break;
						case StorageAccessStatus.NOT_SUPPORTED:
							experienceTracker.abort({
								name: ExperienceNames.STORAGE_ACCESS_PAGE,
								reason: ExperienceReasons.STORAGE_ACCESS_NOT_SUPPORTED,
							});
							dispatch({
								subject: Subjects.STORAGE_ACCESS,
								status: StorageAccessStatus.NOT_SUPPORTED,
							});
							break;
						case StorageAccessStatus.ERROR:
							experienceTracker.fail({
								name: ExperienceNames.STORAGE_ACCESS_PAGE,
								error: new Error(ExperienceReasons.STORAGE_ACCESS_ERROR),
							});
							dispatch({
								subject: Subjects.STORAGE_ACCESS,
								status: StorageAccessStatus.ERROR,
							});
							break;
					}
					break;
				case Subjects.LOGIN_CONFIRMATION:
					switch (result) {
						case UserLoginStatus.SUCCESS:
							experienceTracker.succeed({ name: ExperienceNames.LOGIN_PAGE });
							dispatch({
								subject: Subjects.LOGIN_CONFIRMATION,
								status: UserLoginStatus.SUCCESS,
							});
							break;
					}
					break;
			}
		},
		[experienceTracker, uniqueKey],
	);

	useEffect(() => {
		window.addEventListener('message', messageListener);
		return () => window.removeEventListener('message', messageListener);
	}, [messageListener]);

	return { state, dispatch };
};
